import { InfoCircleOutlined } from '@ant-design/icons';
import { Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import AlertError from '../../../../shared/components/alert-error/alert-error';
import Modal from '../../../../shared/components/modal/modal';
import { useAppSelector } from '../../../../store/hooks';
import { requestPreviewAlertId } from '../../../request/constants/constants';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useArchiveScanRequestMutation } from '../../api/scan-requests-api';
import { scanRequestAlertId, scanRequestArchiveModalId } from '../../constants/constants';
import {
  selectSelectedScanRequestIds,
  selectTempScanRequestId,
  setSelectedScanRequestIds,
} from '../../store/scan-requests-slice';
import styles from './scan-request-archive-modal.module.css';

const ScanRequestArchiveModalContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['scan-requests', 'common']);
  const dispatch = useDispatch();
  const [archiveScanRequest, { isLoading }] = useArchiveScanRequestMutation();
  const selectedScanRequests = useAppSelector(selectSelectedScanRequestIds);
  const [isErrorAlertVisible, setIsErrorAlertVisible] = useState(false);
  const closeFormModal = () => {
    dispatch(setSelectedScanRequestIds([]));
    dispatch(closeModal(scanRequestArchiveModalId));
  };
  const activeScanRequestId = useAppSelector(selectTempScanRequestId);

  const setScanRequestArchiveAlertMessage = (alertId: string) => {
    dispatch(
      setAlert({
        id: alertId,
        type: 'success',
        title: t('scanRequestArchiveModal.message.scanRequestArchived'),
      }),
    );
  };

  const onArchiveSubmit = () => {
    setIsErrorAlertVisible(false);
    const payload = activeScanRequestId ? [activeScanRequestId] : selectedScanRequests.map(id => id);

    archiveScanRequest({ scanRequests: payload })
      .unwrap()
      .then(() => {
        closeFormModal();
        setScanRequestArchiveAlertMessage(scanRequestAlertId);
        setScanRequestArchiveAlertMessage(requestPreviewAlertId);
      })
      .catch(() => setIsErrorAlertVisible(true));
  };

  return (
    <Modal
      title={t('scanRequestArchiveModal.title')}
      onCancel={closeFormModal}
      isLoading={isLoading}
      customSubmitText={t('signScanRequest.action.uploadToServer', { ns: 'common' })}
      onOk={onArchiveSubmit}
      isOpen={isOpen}
      afterClose={afterClose}
    >
      {isErrorAlertVisible && <AlertError message={t('scanRequestArchiveModal.validationError.archiveError')} />}
      {t('scanRequestArchiveModal.warning')}
      <div>
        <Typography.Text type="success" className={styles.infoIcon}>
          <InfoCircleOutlined />
        </Typography.Text>
        <Typography.Text type="secondary">
          {t('signScanRequest.hint.infoArchiveDuration', { ns: 'common' })}
        </Typography.Text>
      </div>
    </Modal>
  );
};

const ScanRequestArchiveModal = (): JSX.Element => {
  return <LazyModal modalId={scanRequestArchiveModalId} Component={ScanRequestArchiveModalContent} />;
};

export default ScanRequestArchiveModal;
